import React, { useState, useRef, useEffect } from 'react';
import Select, { components } from 'react-select';
import PropTypes from 'prop-types';

const customStyles = {
  control: provided => ({
    ...provided,
    ':focus-within': {
      boxShadow:
        'inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 5px var(--falcon-soft-primary) !important;'
    }
  }),
  valueContainer: provided => ({
    ...provided,
    display: 'flex',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  })
};

const CustomValueContainer = ({ children, ...props }) => {
  const value = props.getValue();
  const isMulti = props.selectProps.isMulti;

  return (
    <components.ValueContainer {...props}>
      {isMulti && value.length > 2 ? (
        <span>{`${value.length} options selected`}</span>
      ) : (
        children
      )}
    </components.ValueContainer>
  );
};

CustomValueContainer.propTypes = {
  children: PropTypes.node.isRequired,
  getValue: PropTypes.func.isRequired,
  selectProps: PropTypes.object.isRequired
};

const MultiSelect = ({
  options,
  value,
  setSelectedOptions,
  isMulti = false,
  ...props
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef(null);
  // Only add "Select All" option if it's multi-select
  const enhancedOptions =
    isMulti && options?.length
      ? [{ label: 'Select All', value: 'select-all' }, ...(options || [])]
      : options || [];

  const handleChange = selected => {
    if (isMulti) {
      // Handle multi-select case
      const selectAllSelected = selected?.some(
        option => option.value === 'select-all'
      );

      if (selectAllSelected) {
        if (Array.isArray(value) && value.length === options.length) {
          setSelectedOptions([]);
        } else {
          const allOptions = options || [];
          setSelectedOptions(allOptions);
        }
      } else {
        setSelectedOptions(selected || []);
      }
    } else {
      // Handle single-select case
      setSelectedOptions(selected || null);
    }
  };

  const handleClickOutside = event => {
    if (selectRef.current && !selectRef.current.contains(event.target)) {
      setMenuIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle display value based on single/multi select
  const displayValue = isMulti
    ? Array.isArray(value)
      ? value.filter(option => option.value !== 'select-all')
      : []
    : value;

  return (
    <div ref={selectRef}>
      <Select
        className="react-select-container"
        classNamePrefix="select"
        styles={customStyles}
        {...props}
        options={enhancedOptions}
        value={displayValue}
        onChange={handleChange}
        closeMenuOnSelect={!isMulti}
        hideSelectedOptions={false}
        isMulti={isMulti}
        components={{
          ValueContainer: CustomValueContainer
        }}
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
      />
    </div>
  );
};

MultiSelect.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.null
  ]),
  setSelectedOptions: PropTypes.func,
  isMulti: PropTypes.bool
};

export default MultiSelect;
